import { useContext, useEffect, useRef } from 'react';
import { useResponseActionsContext } from './useResponseActionsContext';
import { ResponseActionsContext } from '../../context/ResponseActionsContext';
import {
  ResponseActionHandler,
  ResponseActionScope,
} from '../../types/context';

export const useRegisterResponseAction = (
  name: string,
  handler: ResponseActionHandler,
  // Optional explicit scope — bypasses ScopeContext lookup. Use when the calling
  // component sits outside the desired scope's provider (e.g. SlaveDatasetContext
  // lives in LoggedInLayout above the per-subset ScopeProvider, but its handlers
  // must land in 'subset' scope).
  scopeOverride?: ResponseActionScope
): void => {
  const central = useContext(ResponseActionsContext);
  const scoped = useResponseActionsContext();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // Synchronous register during first render — guaranteed ready before any useEffect (sibling, parent or child) fires.
  // Ref-guard avoids duplicate register under StrictMode double-invoke.
  const unregisterRef = useRef<(() => void) | undefined>(undefined);
  if (!unregisterRef.current) {
    const wrappedHandler = (params: unknown) => handlerRef.current(params);
    unregisterRef.current = scopeOverride
      ? central.registerResponseAction(scopeOverride, name, wrappedHandler)
      : scoped.registerResponseAction(name, wrappedHandler);
  }

  useEffect(() => {
    return () => {
      unregisterRef.current?.();
      unregisterRef.current = undefined;
    };
  }, []);
};
